import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FilePlus2, RefreshCw } from 'lucide-react';
import { api, type Customer, type CustomerDetail, type MeResponse, type QuotePayload, type VerifyResult, isSuspendedJob } from '@/lib/api';
import { cn } from '@/lib/utils';
import CustomerPicker from '@/components/CustomerPicker';
import CustomersAdmin from '@/components/CustomersAdmin';
import QuoteList from '@/components/QuoteList';

/**
 * Accueil : le rep choisit le client chez qui il se trouve, puis ouvre une
 * nouvelle commande ou reprend une commande existante de ce client.
 */
export default function Home({ session }: { session: MeResponse }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const isAdmin = session.user.role === 'ADMIN';

  const create = useMutation({
    mutationFn: (custNum: string) =>
      api<QuotePayload>('/api/quotes', { method: 'POST', body: JSON.stringify({ custNum }) }),
    onSuccess: (data) => navigate(`/commandes/${data.quote.id}`),
    // La création relit le compte à l'API (ex. 409 « Compte suspendu ») : la fiche affichée se met à jour.
    onError: () => void qc.invalidateQueries({ queryKey: ['customer'] }),
  });

  const detail = useQuery({
    queryKey: ['customer', customer?.custNum],
    queryFn: () => api<CustomerDetail>(`/api/customers/${encodeURIComponent(customer!.custNum)}`),
    enabled: !!customer,
  });

  const verify = useMutation({
    mutationFn: (custNum: string) =>
      api<VerifyResult>(`/api/customers/${encodeURIComponent(custNum)}/verifier`, { method: 'POST' }),
    onSuccess: (data) => {
      if (data.checked && customer) qc.setQueryData<CustomerDetail>(['customer', customer.custNum], { customer: data.customer, emails: detail.data?.emails ?? [], jobs: data.jobs });
      void qc.invalidateQueries({ queryKey: ['customer'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const verifyMsg = !verify.data
    ? verify.isError
      ? (verify.error as Error).message
      : null
    : !verify.data.checked
      ? 'Vérification impossible pour le moment.'
      : [verify.data.customer, ...verify.data.jobs].some((j) => j.custNum === verify.variables && isSuspendedJob(j))
        ? 'Toujours suspendu. Si la comptabilité vient de le débloquer, réessayez dans quelques minutes.'
        : null;

  if (customer) {
    // Les jobs commandables viennent du détail seulement (jobs que l'utilisateur
    // peut voir) : jamais de bouton de commande tiré de la ligne de la liste.
    const jobs = detail.data?.jobs ?? [];
    // Le détail rafraîchi (après vérification) fait foi sur la fiche de la liste.
    const head = detail.data?.customer ?? customer;
    // Liste des jobs dès qu'il y en a plusieurs, ou que le seul job visible n'est
    // pas la fiche affichée (job 0 compte maison, job 1 au rep) : on voit sur quel compte on commande.
    const multi = jobs.length > 1 || (jobs.length === 1 && jobs[0]!.id !== head.id);
    const addr = (c: Customer) =>
      [c.address, c.city, c.province, c.postalCode].filter(Boolean).join(', ') || 'Adresse non renseignée';
    const newOrderBtn = (c: Customer, full: boolean) => (
      <button
        onClick={() => create.mutate(c.custNum)}
        disabled={create.isPending}
        className={cn(
          'bg-primary text-primary-foreground hover:bg-primary-dark btn-caps flex items-center justify-center gap-2 rounded-lg transition-colors disabled:opacity-50',
          full ? 'h-12 w-full px-6 sm:w-auto' : 'h-11 w-full px-4 sm:w-auto',
        )}
      >
        <FilePlus2 className="size-5" />
        {create.isPending && create.variables === c.custNum ? 'Création…' : 'Nouvelle commande'}
      </button>
    );
    const verifyBlock = (c: Customer) => (
      <div className="space-y-1.5">
        <p className="text-sm">
          <span className="bg-red-50 text-destructive rounded px-1.5 py-px text-xs font-medium">Compte suspendu</span>{' '}
          <span className="text-muted-foreground">Contactez la comptabilité pour le débloquer</span>
        </p>
        <button
          onClick={() => verify.mutate(c.custNum)}
          disabled={verify.isPending}
          className="hover:bg-accent flex h-11 w-full items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold transition-colors disabled:opacity-50 sm:w-auto"
        >
          <RefreshCw className={cn('size-4', verify.isPending && verify.variables === c.custNum && 'animate-spin')} />
          {verify.isPending && verify.variables === c.custNum ? 'Vérification…' : 'Vérifier le compte'}
        </button>
        {verifyMsg && verify.variables === c.custNum && (
          <p className="text-muted-foreground text-sm">{verifyMsg}</p>
        )}
      </div>
    );

    return (
      <>
        <section className="panel space-y-4 p-4 sm:p-5">
          <button
            onClick={() => setCustomer(null)}
            className="text-primary -ml-1 flex h-9 items-center gap-1 rounded-md px-1 text-sm font-medium hover:underline"
          >
            <ArrowLeft className="size-4" />
            Changer de client
          </button>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="page-title">{head.name}</h1>
              <p className="text-muted-foreground mt-1 text-sm tabular-nums">
                <span className="text-foreground font-bold">#{head.custNum}</span>
                {' · '}
                {addr(head)}
              </p>
            </div>
            {!multi && jobs.length === 1 && (isSuspendedJob(jobs[0]!) ? verifyBlock(jobs[0]!) : newOrderBtn(jobs[0]!, true))}
          </div>
          {detail.isPending && <p className="text-muted-foreground text-sm">Chargement de la fiche…</p>}
          {detail.isError && (
            <p className="text-destructive text-sm">
              Fiche indisponible : {(detail.error as Error).message}
            </p>
          )}
          {detail.isSuccess && !jobs.length && (
            <p className="text-muted-foreground text-sm">Aucun compte de ce client ne peut être commandé.</p>
          )}
          {multi && (
            <ul className="divide-y rounded-lg border">
              {jobs.map((j) => (
                <li
                  key={j.id}
                  className={cn(
                    'flex flex-wrap items-center justify-between gap-3 px-3 py-2.5',
                    isSuspendedJob(j) && 'text-muted-foreground bg-muted/40',
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm tabular-nums">
                      <span className={cn('font-bold', !isSuspendedJob(j) && 'text-foreground')}>Job {j.job ?? 0}</span>
                      {' · '}
                      <span>{addr(j)}</span>
                    </p>
                    <p className="text-muted-foreground text-sm tabular-nums">
                      Compte {j.accountNumber ?? j.clientNum ?? j.custNum}
                    </p>
                  </div>
                  {isSuspendedJob(j) ? verifyBlock(j) : newOrderBtn(j, false)}
                </li>
              ))}
            </ul>
          )}
          {create.isError && <p className="text-destructive text-sm">{(create.error as Error).message}</p>}
        </section>
        <QuoteList custNum={customer.custNum} title="Commandes de ce client" />
      </>
    );
  }

  return (
    <>
      <h1 className="page-title">Chez quel client êtes-vous ?</h1>
      <CustomerPicker onPick={setCustomer} defaultScope={isAdmin ? 'all' : 'mine'} />
      <QuoteList title={isAdmin ? 'Commandes récentes (tous les reps)' : 'Mes commandes récentes'} />
      {isAdmin && <CustomersAdmin />}
    </>
  );
}
